import { useLayoutEffect, useRef, type DependencyList, type RefObject } from "react";

export interface TimelineCanvasGeometry {
  /** Local CSS size with fractional layout precision (not integer clientWidth/clientHeight). */
  width: number;
  height: number;
  /** Backing bitmap size: displayed size times device pixel ratio. */
  bitmapWidth: number;
  bitmapHeight: number;
}

/** Positions use local CSS pixels; transforms and DPR only affect bitmap density. */
export function measureTimelineCanvas(canvas: HTMLCanvasElement): TimelineCanvasGeometry | null {
  const style = getComputedStyle(canvas);
  const width = Number.parseFloat(style.width);
  const height = Number.parseFloat(style.height);
  const rect = canvas.getBoundingClientRect();
  if (!(width > 0 && height > 0 && rect.width > 0 && rect.height > 0)) return null;
  const dpr = window.devicePixelRatio || 1;
  return {
    width,
    height,
    bitmapWidth: Math.max(1, Math.round(rect.width * dpr)),
    bitmapHeight: Math.max(1, Math.round(rect.height * dpr)),
  };
}

/** Sizes the bitmap (only when it changed) and maps local CSS pixels onto it. */
export function prepareTimelineCanvas(
  canvas: HTMLCanvasElement,
  geometry: TimelineCanvasGeometry,
): CanvasRenderingContext2D | null {
  const context = canvas.getContext("2d");
  if (!context) return null;
  if (canvas.width !== geometry.bitmapWidth) canvas.width = geometry.bitmapWidth;
  if (canvas.height !== geometry.bitmapHeight) canvas.height = geometry.bitmapHeight;
  context.setTransform(
    geometry.bitmapWidth / geometry.width,
    0,
    0,
    geometry.bitmapHeight / geometry.height,
    0,
    0,
  );
  return context;
}

/**
 * One backing bitmap per row. `draw` runs on the next frame after any of `deps` changes
 * (`invalidated: true`) and after resize, DPR and inherited theme changes (`invalidated: false`,
 * so the caller may skip a repaint whose geometry and palette are unchanged — ancestor style
 * mutations are often unrelated, such as the playhead's CSS position). Notifications within one
 * frame coalesce into a single draw of the latest committed view. `draw` returns true when
 * paint is current, false when it cannot paint yet (e.g. a hidden, zero-size canvas). A deferred
 * invalidation survives until a later notification can paint; it does not spin a frame loop.
 */
export function useTimelineRowCanvas(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  draw: (canvas: HTMLCanvasElement, invalidated: boolean) => boolean,
  deps: DependencyList,
): void {
  const drawRef = useRef(draw);
  // 렌더 중인 데이터는 Suspense에서 폐기될 수 있다. 외부 rAF에는 commit된 콜백만 노출한다.
  useLayoutEffect(() => {
    drawRef.current = draw;
  });
  const frameRef = useRef<number | undefined>(undefined);
  const invalidatedRef = useRef(true);
  const scheduleRef = useRef((invalidated: boolean) => {
    invalidatedRef.current ||= invalidated;
    if (frameRef.current !== undefined) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined;
      const canvas = canvasRef.current;
      if (canvas && drawRef.current(canvas, invalidatedRef.current)) {
        invalidatedRef.current = false;
      }
    });
  });

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const schedule = () => scheduleRef.current(false);
    const resize = new ResizeObserver(schedule);
    resize.observe(canvas);
    const themeObserver = new MutationObserver(schedule);
    // Tokens may be overridden anywhere along the inheritance chain. Do not observe the
    // subtree: playback/selection descendants update independently of this static paint.
    for (let scope: Element | null = canvas; scope; scope = scope.parentElement) {
      themeObserver.observe(scope, { attributeFilter: ["data-c6-theme", "style", "class"] });
    }
    let resolution: MediaQueryList;
    const watchResolution = () => {
      resolution?.removeEventListener("change", watchResolution);
      resolution = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      resolution.addEventListener("change", watchResolution);
      schedule();
    };
    watchResolution();
    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
      frameRef.current = undefined;
      resize.disconnect();
      themeObserver.disconnect();
      resolution.removeEventListener("change", watchResolution);
    };
  }, [canvasRef]);

  useLayoutEffect(() => {
    scheduleRef.current(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller's invalidation key.
  }, deps);
}
